<template>
    <list-container fixed>
        <template #header>
            <list-cell-header :width="20">
                <template #title>
                    Naam
                </template>
            </list-cell-header>
            <list-cell-header :width="30">
                <template #title>
                    E-mailadres
                </template>
            </list-cell-header>
            <list-cell-header :width="25">
                <template #title>
                    Gekoppelde vestiging(en)
                </template>
            </list-cell-header>
            <list-cell-header :width="10">
                <template #title>
                    {{ hasWinckBranding() ? 'Winck' : 'AskNoa' }} toegang
                </template>
            </list-cell-header>
            <list-cell-header :width="10">
                <template #title>
                    Beheerder
                </template>
            </list-cell-header>
            <list-cell-header :width="5" />
        </template>
        <template #content>
            <list-row
                v-for="(user, index) in sortedUsers"
                :key="user.id.value"
                :background-color="index % 2 ? 'gray-100' : 'white'"
                @mouseenter="setCurrentUserId(user.id)"
                @focusin="setCurrentUserId(user.id)"
                @mouseleave="resetCurrentUserId()"
                @focusout="resetCurrentUserId()"
            >
                <list-cell-text>
                    <template #content>
                        {{ user.name }}
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        {{ user.email }}
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        {{ user.winckTeams.map((team) => team.name).splice(0, 2).join(', ') }}
                        <template v-if="user.winckTeams.length - 2 > 0">
                            +{{ user.winckTeams.length - 2 }}
                        </template>
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <svg-icon
                            :icon="user.keyflowRole === null ? ShieldSlashIcon : ShieldCheckIcon"
                            :color="user.keyflowRole === null ? 'gray-300' : 'green-300'"
                            is-custom-icon
                        />
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <svg-icon
                            :icon="ManageAccountsIcon"
                            :color="KeyflowRole.COMPANY_MANAGER.equals(user.keyflowRole ?? '') ? 'green-300' : 'gray-300'"
                        />
                    </template>
                </list-cell-text>
                <list-cell>
                    <flex-container
                        v-show="user.id.equals(currentUserId)"
                        height="full"
                        align-content="center"
                        justify-content="end"
                        style="padding-right: 10px"
                    >
                        <collapse-box-decorator
                            horizontal-position="right"
                        >
                            <template #default>
                                <svg-icon
                                    :icon="MoreVertIcon"
                                    :size="IconSize.lg"
                                    :color="'primary'"
                                />
                            </template>
                            <template #content>
                                <collapse-option-container
                                    @mouseenter="setCurrentUserId(user.id)"
                                    @focusin="setCurrentUserId(user.id)"
                                    @mouseleave="resetCurrentUserId()"
                                    @focusout="resetCurrentUserId()"
                                >
                                    <collapse-option @click="$router.push({ name: 'user-edit', params: { userId: user.id.value } })">
                                        Bewerken
                                    </collapse-option>
                                    <collapse-option @click="$router.push({ name: 'user-delete', params: { userId: user.id.value } })">
                                        <text-decorator :color="'red-500'">
                                            Verwijderen
                                        </text-decorator>
                                    </collapse-option>
                                </collapse-option-container>
                            </template>
                        </collapse-box-decorator>
                    </flex-container>
                </list-cell>
            </list-row>
        </template>
    </list-container>
</template>

<script setup lang="ts">
    import { IconSize } from '@keyvue/one-ui';
    import MoreVertIcon from '@keyvue/one-material-icons/icons/more_vert.svg?component';
    import ShieldCheckIcon from '@keyvue/one-awesome-icons/icons/regular/shield-check.svg?component';
    import ShieldSlashIcon from '@keyvue/one-awesome-icons/icons/regular/shield-slash.svg?component';
    import ManageAccountsIcon from '@keyvue/one-material-icons/icons/manage_accounts.svg?component';
    import { KeyflowRole, type User, type UserId } from '@keyvue/one-api-client';
    import { computed, ref } from 'vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    export interface Props {
        users: User[]
    }

    const props = defineProps<Props>();

    const { hasWinckBranding } = useAppBranding();

    const currentUserId = ref<null | UserId>(null);
    const currentThrottle = ref<undefined | number>(undefined);

    function setCurrentUserId(id: UserId) {
        clearTimeout(currentThrottle.value);
        currentThrottle.value = undefined;
        currentUserId.value = id;
    }

    function resetCurrentUserId() {
        currentThrottle.value = setTimeout(() => {
            currentUserId.value = null;
        }, 50) as unknown as number;
    }

    const sortedUsers = computed(() => ([...props.users].sort((a, b) => a.name.fullName.localeCompare(b.name.fullName))));
</script>
